import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { UserProvider, UserContext } from './UserContext';
import { SnackbarProvider } from 'notistack';
import { useContext } from 'react';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Account from './pages/Account';
import Billing from './pages/Billing';
import Conversations from './pages/Conversations';
import Assistants from './pages/Assistants';
import PlatformAdmin from './pages/Admin';
import Tools from './pages/Tools';
import Knowledge from './pages/Knowledge';
import BusinessAssistants from './pages/BusinessAssistants';
import Workflow from './pages/workflow';
import LeadFlow from './pages/LeadFlow';
import ResetPassword from './pages/ResetPassword';
import SubscriptionSetup from './pages/SubscriptionSetup';
import AssistantChatSetup from './pages/AssistantChatSetup';
import CompleteSetup from './pages/CompleteSetup';
import ChangePassword from './pages/ChangePassword';
import SignupCheckEmail from './pages/SignupCheckEmail';
import SsoComplete from './pages/SsoComplete';
import SsoError from './pages/SsoError';
import SsoSettings from './pages/SsoSettings';
import ProtectedRoute from './components/ProtectedRoute';
import LeadFlowProtectedRoute from './components/LeadFlowProtectedRoute';
import DevChat from './pages/DevChat'; // <-- 1. Import the new page
import Guardrails from './pages/Guardrails';
import './App.css';

// Root redirect component that uses UserContext instead of localStorage
const RootRedirect = () => {
  const { userId, mustChangePassword } = useContext(UserContext);
  if (!userId) return <Navigate to="/login" replace />;
  return <Navigate to={mustChangePassword ? "/change-password" : "/dashboard"} replace />;
};

/* The forced change-password screen needs a session but must NOT go through
   ProtectedRoute, that redirects here whenever the flag is set, which would
   loop. Signed-out users still get bounced to login; users who have already
   changed their password have no reason to be here, so they go to the
   dashboard rather than being shown a second, pointless password prompt. */
const ChangePasswordRoute = () => {
  const { userId, mustChangePassword } = useContext(UserContext);
  if (!userId) return <Navigate to="/login" replace />;
  if (!mustChangePassword) return <Navigate to="/dashboard" replace />;
  return <ChangePassword />;
};

function App() {
  return (
    <UserProvider>
      <SnackbarProvider maxSnack={3}>
        <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/agentchat" element={<AssistantChatSetup/>}/>
          <Route path="/subscription-setup" element={<SubscriptionSetup />} />
          <Route path="/subscription" element={<SubscriptionSetup />} /> {/* Alias for cleaner URLs */}
          <Route path="/complete-setup/:userId" element={<CompleteSetup />} />
          {/* Terminal signup screen. Public: signup ends signed OUT, so there
              is no session to protect it with. */}
          <Route path="/signup/check-email" element={<SignupCheckEmail />} />

          {/* Microsoft sign-in lands here. Both are deliberately public: they
              run before a session exists, so putting them behind the auth
              guard produces a redirect loop. */}
          <Route path="/auth/sso/complete" element={<SsoComplete />} />
          <Route path="/auth/sso/error" element={<SsoError />} />

          {/* Session-only, deliberately outside the ProtectedRoute group */}
          <Route path="/change-password" element={<ChangePasswordRoute />} />

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/account" element={<Account />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/assistants" element={<Assistants />} />
            <Route path="/conversations" element={<Conversations />} />
            <Route path="/admin" element={<PlatformAdmin />} />
            {/* Where the backend's Entra link callback redirects on success,
                with ?linked=1. Protected: the page's own endpoints all
                require a token and re-check org-admin server-side. */}
            <Route path="/settings/sso" element={<SsoSettings />} />
            <Route path="/knowledge" element={<Knowledge />} />
            <Route path="/businessassistants" element={<BusinessAssistants />} />
            <Route path="/tools" element={<Tools />} />
            {/*Adding new workflow route so that workflow can be called*/}
            <Route path="/workflow" element={<Workflow />} />
            {/* 2. Add the new route for the dev chat page */}
            <Route path="/dev-chat" element={<DevChat />} /> 
            <Route path="/guardrails" element={<Guardrails />} />
              <Route element={<LeadFlowProtectedRoute />}>
                <Route path="/leadflow" element={<LeadFlow />} />
              </Route>

          </Route>

          <Route element={<ProtectedRoute withLayout={false} />}>
            <Route
              path="/businessassistants/:assistantId"
              element={<BusinessAssistants />}
            />
          </Route>

          {/* Redirect root to dashboard or login based on auth state */}
          <Route path="/" element={<RootRedirect />} />
        </Routes>
        </Router>
      </SnackbarProvider>
    </UserProvider>
  );
}

export default App;
